/**
 * Document model: strokes, elements (text/images), layers, bookmarks.
 *
 * Every object lives inside a cell (see cell.ts) and is expressed in that
 * cell's local coordinates. `size`/`width` values are in the same local
 * units, so content drawn deep in the tree is physically tiny relative to
 * content near the root — this is what makes "zoom in and draw smaller
 * forever" work.
 */

import type { Vec2 } from "./math";

export type ToolKind =
  | "pencil"
  | "pen"
  | "marker"
  | "highlighter"
  | "eraser"
  | "fill"
  | "select"
  | "lasso"
  | "line"
  | "rect"
  | "ellipse"
  | "text"
  | "image"
  | "eyedropper"
  | "bookmark";

export type EraserMode = "current" | "above" | "below";

export interface StrokeStyle {
  tool: Exclude<ToolKind, "select" | "eyedropper" | "bookmark" | "image" | "fill">;
  color: string; // css color; ignored for eraser
  /** Base width in the stroke's own cell-local units. */
  size: number;
  opacity: number; // 0..1
  /** For eraser strokes only. */
  eraserMode?: EraserMode;
}

export interface StrokePoint {
  x: number;
  y: number;
  /** 0..1 pointer pressure. 0.5 default for mouse. */
  p: number;
}

export interface Stroke {
  kind: "stroke";
  id: string;
  layerId: string;
  cellId: string;
  points: StrokePoint[];
  style: StrokeStyle;
}

export interface TextElement {
  kind: "text";
  id: string;
  layerId: string;
  cellId: string;
  x: number;
  y: number;
  text: string;
  fontSize: number; // cell-local units (height of the text)
  color: string;
  fontFamily: string;
}

export interface ImageElement {
  kind: "image";
  id: string;
  layerId: string;
  cellId: string;
  x: number;
  y: number;
  w: number;
  h: number;
  /** Data URL. Bounded by MAX_INLINE_IMAGE_BYTES. */
  src: string;
  opacity: number;
}

export interface ShapeElement {
  kind: "shape";
  id: string;
  layerId: string;
  cellId: string;
  shape: "rect" | "ellipse";
  x: number;
  y: number;
  w: number;
  h: number;
  color: string;
  size: number;
  opacity: number;
  filled: boolean;
}

/**
 * Fill: a solid polygon in a cell's local coordinates (paint-bucket output).
 * Created by the fill tool from a clicked closed stroke loop, or by filling
 * an unfilled shape (which instead flips that shape's `filled` flag).
 * Rendered underneath strokes/shapes so outlines stay visible.
 */
export interface FillElement {
  kind: "fill";
  id: string;
  layerId: string;
  cellId: string;
  /** Closed polygon vertices in cell-local units (at least 3). */
  points: Vec2[];
  color: string;
  opacity: number;
}

export type Element = Stroke | TextElement | ImageElement | ShapeElement | FillElement;

export type BlendMode = "normal" | "multiply" | "screen" | "erase";

export interface Layer {
  id: string;
  name: string;
  visible: boolean;
  blend: BlendMode;
  opacity: number;
}

export interface Bookmark {
  id: string;
  cellId: string;
  x: number; // in cell-local coords
  y: number;
  /** px per bookmark-cell unit at creation; restored on jump. Optional for legacy docs. */
  scale?: number;
  label: string;
  createdAt: number;
}

export interface WorldData {
  version: 1;
  layers: Layer[];
  bookmarks: Bookmark[];
  /** All elements indexed by cell id ("" = root). */
  cells: Record<string, Element[]>;
  /** Last camera position, for resume. */
  camera?: {
    focusCellId: string;
    x: number;
    y: number;
    scale: number;
    rotation: number;
  };
}

export const DEFAULT_LAYER: Layer = {
  id: "default",
  name: "Layer 1",
  visible: true,
  blend: "normal",
  opacity: 1,
};

/** Maximum size of an inlined image (data URL) in a document, ~3MB. */
export const MAX_INLINE_IMAGE_BYTES = 3 * 1024 * 1024;
